import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../lib/auth.jsx'
import { api } from '../../lib/api.js'
import { accountSchool } from '../../lib/championDisplay.js'
import { stubCoach } from '../../lib/championToClose.js'
import { championshipCoachSport } from './championshipCoachQuery.js'

// Share the read between the opportunity and its dossier. A new authenticated
// user object gets a separate cache; nothing survives logout or a page reload.
const sessions = new WeakMap()
const MAX_ENTRIES = 100
const FRESH_MS = 60_000

export function useCoachContact(champ, enabled = true) {
  const { user } = useAuth()
  const sport = championshipCoachSport(champ)
  const school = enabled && champ && !champ.sourceType && sport !== null ? accountSchool(champ) : ''
  const query = { school, sport: sport || '', level: champ?.level || '',
    segment: champ?.segment || '', state: champ?.state || '' }
  const key = JSON.stringify([champ?.id, champ?.season, champ?.seasonYear, query])
  const [result, setResult] = useState(null)
  let cache = user && sessions.get(user)
  if (user && !cache) { cache = new Map(); sessions.set(user, cache) }
  const cached = cache?.get(key)
  const current = result?.key === key && result?.user === user ? result : null
  const response = current?.response || (cached?.at > Date.now() - FRESH_MS ? cached.response : null)
  const status = !school ? 'unavailable' : response ? 'done' : current?.status || 'loading'

  useEffect(() => {
    if (!school || !user) return undefined
    let alive = true
    let entry = cache.get(key)
    if (!entry || (!entry.promise && entry.at <= Date.now() - FRESH_MS)) {
      entry = { at: 0 }
      entry.promise = api.coach(query).then(response => {
        entry.response = response
        entry.at = Date.now()
        entry.promise = null
        return response
      }).catch(error => { cache.delete(key); throw error })
      cache.set(key, entry)
      // Never evict an in-flight read that another open surface may share.
      for (const [oldKey, old] of cache) {
        if (cache.size <= MAX_ENTRIES) break
        if (!old.promise) cache.delete(oldKey)
      }
    }
    Promise.resolve(entry.promise || entry.response).then(response => {
      if (alive) setResult({ key, user, status: 'done', response })
    }).catch(() => {
      if (alive) setResult({ key, user, status: 'error', response: null })
    })
    return () => { alive = false }
    // The key contains every query field and the championship/season identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, user, school, cache])

  const named = champ?.story?.coach
  const coach = useMemo(() => {
    const sourced = stubCoach({ story: { coach: named } })
    const found = response?.coach
    return sourced.name ? sourced : found?.name
      ? { ...found, role: found.role || 'Head Coach', email: found.email || '', source: 'directory', confidence: found.confidence ?? 1 }
      : { name: '', role: 'Head Coach', source: 'needs_review',
        reason: sport === null ? 'team_identity_conflict' : !school ? 'school_missing' : status === 'error' ? 'contact_lookup_failed'
          : response?.needs_review?.reason || 'no_head_coach_on_file' }
  }, [named, response, status, school, sport])
  return { key, status, school, coach, response }
}
